// The page a website, such as www.blitz3d.plus, sends the browser to so that
// a member signs in there with their Blitz3D+ account
// (docs/PLUS_MEMBERSHIP_DESIGN.md, "Signing in to the website"):
//   /link/site/?client=<website>&challenge=<challenge>&state=<state>
// It signs the member in with their passkey, if needed, asks whether to
// continue to the website, and sends the browser back to the website's
// return address with a one-time code the server made
// (web/server/lib/sites.mjs), or with error=cancelled.

import { Api, ApiError } from "./api.js";

const api = new Api();
const view = document.getElementById("view");
const params = new URLSearchParams(location.search);
const client = params.get("client") || "";
const challenge = params.get("challenge") || "";
const state = params.get("state") || "";
let site = null;   // { name, host, returnUrl }

function h(tag, attributes = {}, ...children) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null || value === false) continue;
    if (key.startsWith("on")) element.addEventListener(key.slice(2), value);
    else if (key === "class") element.className = value;
    else element.setAttribute(key, value === true ? "" : value);
  }
  for (const child of children.flat()) if (child !== null && child !== undefined) element.append(child);
  return element;
}

const valid = /^[a-z][a-z0-9-]{0,31}$/.test(client) && /^[A-Za-z0-9_-]{43}$/.test(challenge) &&
  /^[A-Za-z0-9_-]{16,64}$/.test(state);
const back = (query) => {
  const url = new URL(site.returnUrl);
  for (const [key, value] of Object.entries(query)) url.searchParams.set(key, value);
  url.searchParams.set("state", state);
  return url.href;
};

function failed(error) {
  if (error && error.name === "NotAllowedError") return;
  if (error instanceof ApiError && error.body.needsPlus) {
    view.append(h("p", { class: "error" }, `${site.host} is for Plus members.`));
    return;
  }
  view.append(h("p", { class: "error" }, error instanceof ApiError ? error.message : String(error && error.message || error)));
}

function cancelButton() {
  const cancel = h("button", { type: "button" }, "Cancel");
  cancel.onclick = () => location.replace(back({ error: "cancelled" }));
  return cancel;
}

async function signInStep() {
  // While accounts are closed (server.mjs --registration), none is made here.
  const config = await api.config().catch(() => ({}));
  const closed = config.registration === "closed";
  const signIn = h("button", { type: "button", class: "primary" }, "Sign in with your passkey");
  const name = h("input", { type: "text", maxlength: "60", "aria-label": "Your name", placeholder: "Your name" });
  const create = h("button", { type: "button" }, "Create an account");
  signIn.onclick = async () => {
    try {
      await api.signIn();
      await start();
    } catch (error) {
      failed(error);
    }
  };
  create.onclick = async () => {
    try {
      await api.register(name.value);
      await start();
    } catch (error) {
      failed(error);
    }
  };
  view.replaceChildren(h("h1", {}, `Sign in to ${site.host}`),
    h("p", {}, "Sign in with your Blitz3D+ passkey: Touch ID, Windows Hello, a phone or a security key."),
    h("div", { class: "toolbar" }, signIn, cancelButton()),
    ...(closed ? [h("p", { class: "muted" }, "New accounts aren't open yet. They will come with Blitz3D+ membership.")] :
      [h("h2", {}, "New here?"), h("div", { class: "toolbar" }, name, create)]));
}

async function continueStep(user) {
  const go = h("button", { type: "button", class: "primary" }, "Continue");
  const cancel = cancelButton();
  const other = h("button", { type: "button", class: "link" }, "Sign in with another passkey");
  go.onclick = async () => {
    go.disabled = cancel.disabled = true;
    try {
      const { redirect } = await api.post("sites/codes", { client, challenge, state });
      view.replaceChildren(h("h1", {}, "Signing in..."), h("p", { class: "muted" }, `Going back to ${site.host}.`));
      location.replace(redirect);
    } catch (error) {
      go.disabled = cancel.disabled = false;
      failed(error);
    }
  };
  other.onclick = async () => {
    try {
      await api.signIn();
      await start();
    } catch (error) {
      failed(error);
    }
  };
  view.replaceChildren(h("h1", {}, `Sign in to ${site.host}`),
    h("p", {}, `Continue to ${site.host} as ${user.name}?`),
    h("div", { class: "toolbar" }, go, cancel),
    h("p", { class: "muted small" }, "Not you? ", other));
}

async function start() {
  const me = await api.me();
  if (!me.user) await signInStep();
  else await continueStep(me.user);
}

if (!valid) {
  view.replaceChildren(h("h1", {}, "This sign-in link isn't complete"),
    h("p", {}, "Go back to the website and press Sign in again."));
} else {
  api.get(`sites/${client}`).then((found) => {
    site = found;
    document.title = `Sign in to ${site.host} - Blitz3D+`;
    return start();
  }).catch((error) => {
    if (!site) {
      view.replaceChildren(h("h1", {}, "This website can't sign in here"),
        h("p", { class: "error" }, error instanceof ApiError ? error.message : String(error && error.message || error)));
      return;
    }
    failed(error);
  });
}
